import { getUnitPrice } from './priceData';
import { getDepartmentCoefficient } from './departmentCoefficients';
import { DetailedEstimateData } from '../../types/estimation/detailed';

export interface DetailedPriceRange {
  min: number;
  max: number;
  sections: Array<{
    id: string;
    title: string;
    total: number;
    items: Array<{
      label: string;
      price: number;
      unit: string;
      details?: string;
    }>;
  }>;
}

const processSelectedItems = (data: any, path: string[] = []): Array<{
  path: string[];
  item: any;
}> => {
  console.log('Processing data:', {
    data,
    initialPath: path
  });

  const selectedItems: Array<{ path: string[]; item: any }> = [];

  const traverse = (obj: any, currentPath: string[] = []) => {
    if (!obj || typeof obj !== 'object') {
      console.log('Skipping non-object:', obj);
      return;
    }

    if (obj.selected === true) {
      const fullPath = [...path, ...currentPath];
      console.log('✅ Selected item found:', {
        path: fullPath,
        item: obj
      });
      selectedItems.push({ 
        path: fullPath,
        item: obj 
      });
      return;
    }

    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === 'object') {
        console.log('🔍 Traversing object:', {
          key,
          currentPath: [...currentPath, key]
        });
        traverse(value, [...currentPath, key]);
      }
    });
  };

  traverse(data, path);
  return selectedItems;
};

const calculateSectionPrice = (
  sectionData: any, 
  sectionId: string,
  coefficients: {
    department: number;
    urgent: number;
    historicArea: number;
    floor: number;
    finishingLevel: number;
  }
): {
  total: number;
  items: Array<{label: string; price: number; unit: string; details?: string}>;
} => {
  const items: Array<any> = [];
  let total = 0;

  console.log('Starting section price calculation:', {
    sectionId,
    coefficients
  });

  const selectedItems = processSelectedItems(sectionData);
  
  console.log('Found selected items:', {
    sectionId,
    selectedItems: selectedItems.map(item => ({
      path: item.path.join('_'),
      value: item.item.value
    }))
  });

  selectedItems.forEach(({ path, item }) => {
    const itemKey = path.join('_').toLowerCase();
    const priceData = getUnitPrice(sectionId, itemKey);
    
    console.log('Looking up price data:', {
      itemKey,
      sectionId,
      found: !!priceData
    });

    if (!priceData) {
      console.error('❌ No price found for item:', {
        itemKey,
        sectionId,
        path: path.join('_')
      });
      return;
    }

    // Calculer le prix avec les coefficients
    const quantity = item.value || 1;
    const basePrice = priceData.price * quantity;
    const adjustedPrice = basePrice * 
      coefficients.department * 
      coefficients.urgent * 
      coefficients.historicArea * 
      coefficients.floor *
      coefficients.finishingLevel;

    // Formatage des détails avec une valeur par défaut
    const details = quantity > 1 ? `${quantity} ${priceData.unit}` : `1 ${priceData.unit}`;

    console.log('💰 Calculated price for item:', {
      itemKey,
      quantity,
      basePrice,
      adjustedPrice,
      coefficients
    });

    total += adjustedPrice;

    items.push({
      label: priceData.name,
      price: adjustedPrice,
      unit: priceData.unit,
      details: details // Toujours définir les détails
    });
  });

  return { total, items };
};

export const calculateDetailedEstimate = (data: DetailedEstimateData): DetailedPriceRange => {
  if (!data) {
    console.warn('No data provided for calculation');
    return { min: 0, max: 0, sections: [] };
  }

  console.log('Starting detailed estimate calculation');

  // Calculer les coefficients une seule fois
  const coefficients = {
    department: data.description?.location?.postcode 
      ? getDepartmentCoefficient(data.description.location.postcode.substring(0, 2))
      : 1,
    urgent: data.description?.constraints?.urgent ? 1.15 : 1,
    historicArea: data.description?.constraints?.historicArea ? 1.2 : 1,
    floor: data.description?.accessibility?.floor > 0 && !data.description?.accessibility?.elevator
      ? 1 + (data.description.accessibility.floor * 0.03)
      : 1,
    finishingLevel: data.description?.finishingLevel === 'premium' ? 1.3 :
                    data.description?.finishingLevel === 'economic' ? 0.8 : 1
  };

  console.log('Applied coefficients:', coefficients);

  // Calculer les prix par section avec les coefficients
  const sections = Object.entries(data)
    .filter(([sectionId]) => sectionId !== 'description' && sectionId !== 'location')
    .map(([sectionId, sectionData]) => {
      const { total, items } = calculateSectionPrice(sectionData, sectionId, coefficients);
      return {
        id: sectionId,
        title: sectionId.charAt(0).toUpperCase() + sectionId.slice(1).replace(/-/g, ' '),
        total,
        items
      };
    })
    .filter(section => section.total > 0);

  console.log('Calculated sections:', sections.map(s => ({
    id: s.id,
    total: s.total,
    itemCount: s.items.length
  })));

  // Calculer le total comme somme des sections
  const totalAmount = sections.reduce((sum, section) => sum + section.total, 0);

  console.log('Final calculation result:', {
    totalAmount,
    sectionCount: sections.length
  });

  return {
    min: Math.round(totalAmount),
    max: Math.round(totalAmount),
    sections
  };
};